/**
 * Refresh the trainer's device copy when the app comes back to the
 * foreground, so the next open draws a transcript that is already current.
 *
 * ── WHO PAYS FOR IT ─────────────────────────────────────────────────────────
 * Only someone who has used the trainer on this device: the refresh runs for
 * the conversation they last OPENED, and only when a copy of it exists. `D156`
 * rejected a history GET on every Today mount for its cost; this is one ticket
 * and one GET per resume at most, throttled to once every five minutes — and
 * skipped while the copy is younger than that, whoever wrote it.
 *
 * Never while the trainer is open: its socket is live and its own read keeps
 * the copy current, and a second read landing after the socket's would write
 * an older transcript over a newer one.
 *
 * It buys its own ticket — tickets are single use (`D34`) — through the
 * provider's ONE `ApiClient` (`useApi`), never a second one, for the refresh
 * reason `use-trainer-chat.ts` gives. Every failure is silent: the copy simply
 * stays as it was.
 *
 * Mounted ONCE, inside the session gate, beside `ClockBridge`
 * (`app/shell.tsx`). This module and `transcript-cache.ts` are all the shell
 * imports from the trainer, so the overlay's chunk stays lazy.
 */
import {
  buyWsTicket,
  type ChatMessage,
  cacheableTail,
  chatHistoryUrl,
  fetchChatHistory,
} from "@repo/client/chat";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { useApi } from "../../data/api";
import { onAppActiveChange } from "../../native";
import { appI18n } from "../auth/i18n";
import { AI_BASE_URL } from "./config";
import {
  cachedTranscriptAt,
  createTranscriptCache,
  isTrainerOpen,
  lastOpenedConversation,
} from "./transcript-cache";

/** At most one refresh per five minutes, and none for a younger copy. */
export const TRANSCRIPT_PREFETCH_INTERVAL_MS = 5 * 60_000;

export interface TranscriptPrefetchPorts {
  readonly isTrainerOpen: () => boolean;
  /** The server's transcript for one conversation; throws on any failure. */
  readonly load: (conversationId: string) => Promise<readonly ChatMessage[]>;
  readonly now: () => number;
  readonly queryClient: QueryClient;
}

/**
 * The refresh, over injected ports so it runs in Node. `run` never rejects.
 * The throttle lives in the returned object: one per mounted bridge.
 */
export function createTranscriptPrefetch(ports: TranscriptPrefetchPorts) {
  const cache = createTranscriptCache(ports.queryClient);
  let lastAttemptAt = Number.NEGATIVE_INFINITY;
  return {
    async run(): Promise<void> {
      const conversationId = lastOpenedConversation(ports.queryClient);
      if (
        conversationId === null ||
        ports.isTrainerOpen() ||
        cache.read(conversationId) === null
      ) {
        return;
      }
      const now = ports.now();
      const newest = Math.max(
        lastAttemptAt,
        cachedTranscriptAt(ports.queryClient, conversationId)
      );
      if (now - newest < TRANSCRIPT_PREFETCH_INTERVAL_MS) {
        return;
      }
      lastAttemptAt = now;
      let messages: readonly ChatMessage[];
      try {
        messages = await ports.load(conversationId);
      } catch {
        return;
      }
      // Opened while the read was in flight: the socket's own read wins.
      if (ports.isTrainerOpen()) {
        return;
      }
      // An empty list is a conversation cleared elsewhere; `write` forgets.
      cache.write(conversationId, cacheableTail(messages));
    },
  };
}

function useTranscriptPrefetch(): void {
  const api = useApi();
  const queryClient = useQueryClient();
  const locale = appI18n.useLocale();

  useEffect(() => {
    const prefetch = createTranscriptPrefetch({
      isTrainerOpen,
      load: async (conversationId) => {
        const { ticket } = await buyWsTicket(api.authorizedFetch, locale);
        const read = await fetchChatHistory(
          chatHistoryUrl(AI_BASE_URL, ticket, conversationId)
        );
        return read.messages;
      },
      now: Date.now,
      queryClient,
    });
    return onAppActiveChange((active) => {
      if (active) {
        prefetch.run().catch(() => undefined);
      }
    });
  }, [api, locale, queryClient]);
}

/** The mount, and the hook's only caller (`app/shell.tsx`). */
export function TranscriptPrefetchBridge(): ReactNode {
  useTranscriptPrefetch();
  return null;
}
